import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import {
  defaultErrorResponses,
  problemResponse,
  registry,
  undeclaredQueryRefused,
} from "../registry.js";
import { z } from "../zod.js";
import {
  QualityControlPoint,
  QualityControlPointCreateRequest,
  QualityControlPointListResponse,
  QualityCutCancelRequest,
  QualityCutConfirmRequest,
  QualityCutEventDetailResponse,
  QualityCutExecuteResponse,
  QualityCutProposeRequest,
  QualityFailureCause,
  QualityFailureCauseCreateRequest,
  QualityFailureCauseCategory,
  QualityFailureCauseListResponse,
  QualityFailureCauseWasteCauseRequest,
  QualityHold,
  QualityHoldDetailResponse,
  QualityHoldDispositionRequest,
  QualityHoldListResponse,
  QualityHoldPlaceRequest,
  QualityHoldStatus,
  QualityInspection,
  QualityInspectionCancelRequest,
  QualityInspectionCompleteRequest,
  QualityInspectionCompleteResponse,
  QualityInspectionCreateRequest,
  QualityInspectionCreateResponse,
  QualityInspectionDetailResponse,
  QualityFailureWorkflowDoor,
  QualityReworkTargetResponse,
  QualityInspectionListResponse,
  QualityInspectionResolveRequest,
  QualityInspectionResultRequest,
  QualityInspectionResultResponse,
  QualityInspectionStatus,
  QualityResolutionResponse,
  QualityResolutionStatus,
  QualityShipReadinessResponse,
  QualityStage,
  QualityVerticalPackSeedRequest,
  QualityVerticalPackSeedResponse,
  QualityCoaDocumentListResponse,
  QualityCoaDocumentStatus,
  QualityCoaDraftRequest,
  QualityCoaDraftResponse,
  QualityCoaIssueResponse,
  QualityCoaRevokeRequest,
  QualityCoaRevokeResponse,
  QualityCoaTemplateListResponse,
  QualityCoaTemplateUpsertRequest,
  QualityCoaTemplate,
  QualityAnalyticsRefreshResponse,
  QualityFpyResponse,
  QualityParetoResponse,
  QualityShiftScoreboardResponse,
  QualitySupplierReceivingResponse,
} from "./schemas.js";

// Module 07 — quality HTTP surface, SLICE 07-A: THE SPINE (spec 07 §5's
// control-point rows, the vertical-pack seed, the two failure-cause rows, the
// inspection surface this slice owns — queue, ad-hoc create, detail, results,
// complete, cancel — and `GET /ship-readiness`), PLUS SLICE 07-B-1's three
// quarantine-hold rows (holds list, manual place, hold detail), PLUS SLICE
// 07-B-2's two disposition rows (`POST /holds/{id}/disposition`,
// `POST /inspections/{id}/resolve` — the whole §3.5 surface minus the cut).
//
// PLUS SLICE 07-C's FIVE GUIDED-CUT ROWS (§2.8/§2.9/§3.5d — propose, confirm,
// execute, cancel and the cut detail read).
//
// WHAT IS ABSENT, AND WHY THAT IS THE POINT. §5's other rows are NOT registered
// here and their paths are absent from `openapi/v1.json` entirely: the CoA +
// analytics rows are 07-E's. A 404 on a route that does not exist is honest; a
// 501 stub that looks like a module is not (D-026, D-033, and the identical
// statement slices 04-A and 05-A wrote). It also keeps every openapi/v1.json
// change additive — the oasdiff gate.
//
// `GET /v1/quality/ship-readiness` IS registered although module 09 does not
// exist to call it. That is §0.2's ruling, not an oversight: it is the whole of
// the contract module 09 will consume, it is drivable by API test today, and
// M07-S05 is amended in place to assert the readiness flip across an inspection
// rather than a ship confirmation no module can perform (§0.1). A caller-less
// READ that a walkthrough drives is not the D-026 stub shape — a disabled shell
// with no behaviour is.
//
// PERMISSIONS, straight off §7's action matrix (see
// 0034_quality_control_points_inspections.sql for the cell-by-cell
// transcription and the seeded role defaults):
//   * `quality.catalog.read` — the control-point library (list + detail).
//     floor_worker's "assigned stage" narrowing is SERVICE-side (the D-040
//     two-layer pattern), never a second code.
//   * `quality.catalog.manage` — create, version and deactivate control points
//     AND write the failure-cause catalog: §7 gives both rows the same role set
//     (group_admin / facility_admin / qc), so by D-074's one-code-per-distinct-
//     role-set rule they are one code — the shape module 05 gave three catalogs
//     in `waste.catalog.manage`.
//   * `quality.vertical_pack.seed` — group_admin alone, a role set of one.
//   * `quality.inspection.read` / `.create` / `.record` / `.cancel` — four
//     distinct §7 role sets, and `.cancel` is deliberately a SECOND code with
//     the same set as `catalog.manage` (0029's precedent: the rule is one code
//     per DISTINCT role set, not one code per role set, and a pack that grants
//     a custom qc-clerk role the control-point library must not silently
//     acquire the authority to cancel a check somebody is executing).
//   * The failure-cause READ carries NO code at all — §5 says "any
//     authenticated", exactly as module 05's catalog reads do, because the
//     floor's cause picker is driven by principals whose §7 read cells are "—".
//   * `quality.hold.read` / `quality.hold.place` — 07-B-1's two (see
//     permissions.ts, migration-seeded alongside 07-B's other three). The read
//     is its own code by D8: §5's holds role set — qc/fa/planner/procurement/
//     finance/sales — matches no existing code, and D-074's rule is one code
//     per DISTINCT role set. `value_at_risk_minor` + `currency` are BOTH
//     absent when redacted (D-126 — serializer role-keyed, never a grant).
//   * `quality.hold.disposition` / `quality.resolution.rework` /
//     `quality.resolution.dispose` — seeded by 0036 route-less; 07-B-2 mounts
//     their routes. The register carries ONE code per route (the invariant
//     every module keeps), so the disposition route checks
//     `quality.hold.disposition` with procurement's return_to_supplier-only
//     narrowing service-enforced, and the resolve route checks
//     `quality.resolution.rework` with `quality.resolution.dispose`
//     additionally required for kind scrap|accept_deviation, service-enforced
//     (D-040's two-layer pattern, both stated in the seeded descriptions).
//   * `quality.cut.execute` — 07-C's ONE code, on ALL FIVE cut routes (D-163).
//     §5 gave `cancel` to qc alone while §7's row and the catalog give the
//     whole surface to fa/qc/floor_worker (+ ga through the spec 16 §2.8
//     invariant); D-074's rule is one code per DISTINCT role set and the
//     register's invariant is one code per route, so a second code for one
//     route's narrower set would cost a grant to seed, mirror, test and
//     explain and buy nothing a service check does not give. CANCEL IS
//     NARROWED SERVICE-SIDE to {group_admin, facility_admin, qc} — D-040's
//     two-layer pattern again — and a floor_worker who proposes is never
//     stranded by it, because D-162's supersede rule means their next propose
//     replaces the live one. The DETAIL READ carries the same code rather than
//     a read code of its own: a cut event is not a board anyone browses, it is
//     the staged state of a physical act, and its role set is the act's.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["quality"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});
const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});
const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });

/**
 * `?active=true|false`; absent means BOTH.
 *
 * A TWO-VALUE STRING ENUM, not `z.coerce.boolean()`, for the reason module 05
 * wrote down after publishing the coercing schema by mistake: `?active=no`
 * coerces to `true` under `z.coerce.boolean()` and would answer a different
 * question from the one asked, on a screen that decides which control points a
 * factory sees.
 */
const BoolQuery = z.enum(["true", "false"]).optional();

// ===========================================================================
// control points (spec 07 §2.1–§2.3, §5 rows 1–5)
// ===========================================================================

route({
  method: "get",
  path: "/v1/quality/control-points",
  operationId: "listQualityControlPoints",
  summary: "The control-point library (§4.1)",
  permission: "quality.catalog.read",
  description:
    "Spec 07 §5 — every row carries its bindings AND its check items: a control point without its checks is not a definition of anything, and §4.1's library renders the bindings summary from exactly this payload. NOT PAGINATED, deliberately: this is a bounded definition set (the cable pack ships four points) that the floor PWA must hold whole to execute an inspection offline, and a paged catalog means a device that has some of the checks. `is_current=true` by default — pass `is_current=false` to see the superseded chain a version bump left behind (§3.1 step 5).",
  request: {
    query: z.object({
      stage: QualityStage.optional(),
      facility_id: z.guid().optional(),
      active: BoolQuery,
      is_current: BoolQuery,
      source: z.enum(["vertical_pack", "custom"]).optional(),
      q: z.string().max(200).optional(),
    }),
  },
  responses: ok("The control-point library", QualityControlPointListResponse),
});

route({
  method: "post",
  path: "/v1/quality/control-points",
  operationId: "createQualityControlPoint",
  summary: "Create a control point (version 1)",
  permission: "quality.catalog.manage",
  description:
    "Spec 07 §3.1 steps 1–4 — the point, its bindings and its ordered check items are created in ONE transaction at `version = 1`, `is_current = true`. `source` is always `custom` on a created row: only the vertical-pack seed writes `vertical_pack`. Binding rules are stage-specific and enforced by the database as well as the service (§2.2): receiving binds material and/or supplier and never a product, final binds a product, and an IN-PROCESS point is bound nowhere here — its binding is module 01's `routing_steps.qc_control_point_id` (01 §2.11), set in the Catalogue routing editor.",
  request: jsonBody(QualityControlPointCreateRequest),
  responses: {
    ...created("The created control point", QualityControlPoint),
    409: problemResponse("QUALITY_CONTROL_POINT_CODE_TAKEN — UNIQUE(tenant, code, version)"),
    422: problemResponse(
      "QUALITY_BINDING_STAGE_INVALID / QUALITY_SAMPLING_PLAN_INVALID / QUALITY_SAMPLING_PLAN_UNAPPLICABLE — #127/D-210: an `aql` receiving plan whose bindings resolve to ≥1 product, every one bulk-grained (tracking none/lot); no receipt could ever satisfy it, so it is refused at configuration time with both remedies named (change the plan, or the material's tracking) / QUALITY_CHECK_ITEM_INVALID / FACILITY_NOT_FOUND",
    ),
  },
});

route({
  method: "get",
  path: "/v1/quality/control-points/{id}",
  operationId: "getQualityControlPoint",
  summary: "Control point detail with check items and bindings",
  permission: "quality.catalog.read",
  request: { params: IdParam },
  responses: {
    ...ok("The control point", QualityControlPoint),
    404: problemResponse(
      "QUALITY_CONTROL_POINT_NOT_FOUND (cross-tenant and out-of-fence ids are invisible)",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/control-points/{id}/versions",
  operationId: "createQualityControlPointVersion",
  summary: "Edit as a new version (N+1)",
  permission: "quality.catalog.manage",
  description:
    "Spec 07 §3.1 step 5 — an edit NEVER mutates a definition an inspection may have pinned. The service creates version N+1 (new row + cloned bindings + cloned check items with the edits) and flips the old row `is_current = false, superseded_by_id = new` in the same transaction, so at most one version of a code is ever current. Open inspections keep executing and grading against THEIR pinned version, which is what M07-S12 asserts: an inspection opened on v2 still grades against v2's bounds after v3 tightens them. Versioning a row that is not current is refused 409 — the chain would fork.",
  request: { params: IdParam, ...jsonBody(QualityControlPointCreateRequest) },
  responses: {
    ...created("The new version (a new id, version N+1)", QualityControlPoint),
    404: problemResponse("QUALITY_CONTROL_POINT_NOT_FOUND"),
    409: problemResponse("QUALITY_CONTROL_POINT_NOT_CURRENT"),
    422: problemResponse(
      "QUALITY_BINDING_STAGE_INVALID / QUALITY_SAMPLING_PLAN_INVALID / QUALITY_SAMPLING_PLAN_UNAPPLICABLE (#127/D-210 — see the create route) / QUALITY_CHECK_ITEM_INVALID",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/control-points/{id}/deactivate",
  operationId: "deactivateQualityControlPoint",
  summary: "Stop a control point triggering",
  permission: "quality.catalog.manage",
  description:
    "Spec 07 §3.1 step 6 — `active = false` on the current version stops FUTURE triggering; historical inspections are unaffected and an open one still resolves normally (§9.7's blocking-deadlock case). Deactivation is not deletion: the definition is the evidence of what a passed inspection actually checked.",
  request: { params: IdParam },
  responses: {
    ...ok("The deactivated control point", QualityControlPoint),
    404: problemResponse("QUALITY_CONTROL_POINT_NOT_FOUND"),
  },
});

// ===========================================================================
// vertical-pack seeding (spec 07 §3.1, §5 row 6)
// ===========================================================================

route({
  method: "post",
  path: "/v1/quality/vertical-pack/seed",
  operationId: "seedQualityVerticalPack",
  summary: "Seed the vertical pack's control points and failure causes",
  permission: "quality.vertical_pack.seed",
  description:
    "Spec 07 §3.1 — called by the module-02 onboarding wizard. Creates control points (`source='vertical_pack'`) from the pack's `qc_checklists` collection plus the failure-cause catalog from its `failure_causes` collection. IDEMPOTENT PER CODE (§9.15): a row this tenant already has is counted as skipped and never overwritten, so a re-install adds what is missing and touches nothing an operator has edited. The `pack` key is a shipped pack of `packages/verticals` — `cable-wire` is the one whose checklists this module seeds from; spec 07 §5's `pack='cable'` names no shipped pack.",
  request: jsonBody(QualityVerticalPackSeedRequest),
  responses: {
    ...ok("What was created and what was already there", QualityVerticalPackSeedResponse),
    422: problemResponse("QUALITY_VERTICAL_PACK_UNKNOWN — not a pack this platform ships"),
  },
});

// ===========================================================================
// failure causes (spec 07 §2.4, §5 rows 7–8)
// ===========================================================================

route({
  method: "get",
  path: "/v1/quality/failure-causes",
  operationId: "listQualityFailureCauses",
  summary: "The failure-cause catalog",
  description:
    "Spec 07 §5 — readable by ANY authenticated principal, and carrying no permission code for the same reason module 05's catalog reads carry none: the §4.4 failure-cause picker is driven by floor workers and storekeepers whose §7 read cells are “—”, and a cause they cannot list is a completion they cannot finish. Not paginated (a bounded catalog the floor holds offline); ordered by code so the picker's grouping is stable. Every row carries `waste_cause_code` (nullable — exposed 2026-08-20, fix #117): `null` says this cause is UNMAPPED and a scrap resolution against it will answer 422 QUALITY_SCRAP_CAUSE_UNMAPPED, which is the fact §4.1b's catalog screen renders and the reason the read exists in this shape.",
  request: {
    query: z.object({ category: QualityFailureCauseCategory.optional(), active: BoolQuery }),
  },
  responses: ok("The cause catalog", QualityFailureCauseListResponse),
});

route({
  method: "post",
  path: "/v1/quality/failure-causes",
  operationId: "createQualityFailureCause",
  summary: "Add a custom failure cause",
  permission: "quality.catalog.manage",
  description:
    "Spec 07 §5 row 8. `code` is the stable identity a pack re-install and every analytics grouping address the row by, so it is not editable once created — the same rule module 05 states for its catalog codes. `waste_cause_code` (optional, additive — 07-B-2 fix round R6) sets the D-132 QC-cause → waste-cause mapping at birth, validated against module 05's own catalog. An EXISTING cause is mapped through the sub-resource row below rather than by creating a duplicate under a second code.",
  request: jsonBody(QualityFailureCauseCreateRequest),
  responses: {
    ...created("The created cause", QualityFailureCause),
    409: problemResponse("QUALITY_FAILURE_CAUSE_CODE_TAKEN — UNIQUE(tenant, code)"),
    422: problemResponse(
      "VALIDATION_FAILED — waste_cause_code does not resolve to an active module-05 waste cause (D-132 pt 3)",
    ),
  },
});

route({
  method: "put",
  path: "/v1/quality/failure-causes/{id}/waste-cause",
  operationId: "setQualityFailureCauseWasteCause",
  summary: "Map an existing failure cause onto a module-05 waste cause",
  permission: "quality.catalog.manage",
  description:
    "Spec 07 §5 row 8a (added 2026-08-20, fix #117 — the hole this closes was live on staging: a qc picked a pack cause, got 422 QUALITY_SCRAP_CAUSE_UNMAPPED telling them to map it, and no route could). D-132 pt 5 is why the row exists at all: the pack re-seed is ON CONFLICT DO NOTHING, so a cause seeded before migration 0037 keeps `waste_cause_code = NULL` through every re-install, and creating a DUPLICATE cause under a second code — the only remedy that existed — splits a factory's Pareto in two forever. A SUB-RESOURCE PUT, not an update of the cause: `code`, `category` and `name_i18n` stay immutable BY THE URL rather than by a whitelist inside a handler, and PUT is the honest verb because sending the same code twice is the same end state (the platform's shape for setting one narrow attribute — `PUT /v1/inventory/cycle-counts/{id}/lines/{lineId}/count`, `PUT /v1/onboarding/steps/{step_no}/payload`). The code is validated against module 05's LIVE catalog through that module's own `causeCodeIsActiveInTxn`, in the same transaction as the write, never against a literal list; the change is audit-chained with its before-image, and there is no un-map (see the request schema). Mapping serves FUTURE inspections — an inspection that already failed keeps the cause it pinned, and re-resolving it now succeeds because the resolution reads the cause row at resolve time.",
  request: { params: IdParam, ...jsonBody(QualityFailureCauseWasteCauseRequest) },
  responses: {
    ...ok("The cause, now carrying its mapping", QualityFailureCause),
    404: problemResponse("QUALITY_FAILURE_CAUSE_NOT_FOUND — unknown, or outside this tenant"),
    422: problemResponse(
      "VALIDATION_FAILED — waste_cause_code does not resolve to an ACTIVE module-05 waste cause in this tenant (D-132 pt 3); the refusal names the read that lists the legal codes",
    ),
  },
});

// ===========================================================================
// inspections (spec 07 §2.5, §2.6, §3.4, §5 rows 9–14)
// ===========================================================================

route({
  method: "get",
  path: "/v1/quality/inspections",
  operationId: "listQualityInspections",
  summary: "The inspection queue (§4.3)",
  permission: "quality.inspection.read",
  description:
    "Spec 07 §5 — cursor-paginated, newest first, ≤200 per page. This IS an unbounded collection (unlike the two catalogs above), so it pages. The §7 narrowings ride on top of the single code, service-side (D-040's two-layer pattern): sales sees final-stage rows only, procurement receiving-stage only, and a floor principal its own facility — the last of which the RLS facility fence already enforces at the database floor.",
  request: {
    query: z.object({
      status: QualityInspectionStatus.optional(),
      resolution_status: QualityResolutionStatus.optional(),
      stage: QualityStage.optional(),
      facility_id: z.guid().optional(),
      control_point_id: z.guid().optional(),
      work_center_id: z.guid().optional(),
      work_order_id: z.guid().optional(),
      work_order_operation_id: z.guid().optional(),
      supplier_id: z.guid().optional(),
      product_id: z.guid().optional(),
      stock_unit_id: z.guid().optional(),
      lot_id: z.guid().optional(),
      completed_from: z.string().optional(),
      completed_to: z.string().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("The queue page", QualityInspectionListResponse),
});

route({
  method: "post",
  path: "/v1/quality/inspections",
  operationId: "createQualityInspection",
  summary: "Create an ad-hoc inspection",
  permission: "quality.inspection.create",
  description:
    "Spec 07 §3.2's manual creation — an inspection against any ACTIVE, CURRENT control point and any subject the principal can see. The version executed is pinned at creation (`control_point_id` names the row, not the code), which is what makes M07-S12 true. Idempotent on `client_idempotency_key`, the floor outbox's replay identity: a replay returns the ORIGINAL inspection with `idempotent_replay: true` rather than minting a second inspection number (§9.1). The replay answers only the user who created the inspection, and only for the same create, compared on the fields the create stores and nothing later rewrites. The note is not compared, because completion writes its own over it. Any other caller, and that user acting through an impersonation grant, gets 409 `IDEMPOTENCY_KEY_REUSE` with nothing from the inspection, before the facility-scope check and the stage fence (D-303). Subject refs are validated through their owning modules' service APIs — they are plain ids, never foreign keys (D-123 ruling 11).",
  request: jsonBody(QualityInspectionCreateRequest),
  responses: {
    ...created("The inspection and its pinned checklist", QualityInspectionCreateResponse),
    404: problemResponse("QUALITY_CONTROL_POINT_NOT_FOUND"),
    422: problemResponse(
      "QUALITY_CONTROL_POINT_INACTIVE / QUALITY_SUBJECT_INVALID / FACILITY_NOT_FOUND",
    ),
  },
});

route({
  method: "get",
  path: "/v1/quality/inspections/{id}",
  operationId: "getQualityInspection",
  summary: "Inspection detail with results and the PINNED version's check items",
  permission: "quality.inspection.read",
  description:
    "Spec 07 §5 — the check items returned are those of the version this inspection PINNED, not the current one. A client that fetched the current definition instead would render the wrong bounds the moment somebody tightens a limit (M07-S12), which is a wrong verdict rather than a stale screen. SINCE 07-C the response also carries `guided_cut` (D-158): §3.5d's eligibility, the source's genealogy depth (original = 0; the cap refuses a propose at ≥ 5), the reason it is not available, the pinned point's trim and minimum, and the id of any LIVE cut — computed server-side by a bounded (≤6) ancestor walk through module 06's own service, never its tables. That block is why the §4.4 failure-action sheet's Guided-cut button and the per-item fault-position input are never client-side derivations, and why a phone that opened this inspection while online can still answer “is a cut allowed on this, and why not” in airplane mode — a client-side ancestor walk could not. SINCE D-299 it also carries `rework_target_work_order_status`: the status of the work order that owns the inspection's own operation, null when the inspection names none, so the §4.4 sheet and the §4.6 dialog can render a Rework that §3.5a would refuse (`blocked_by: work_order_not_live`) as dead with its reason. It is a read; the resolve and disposition doors remain the authority.",
  request: { params: IdParam },
  responses: {
    ...ok("The inspection", QualityInspectionDetailResponse),
    404: problemResponse("QUALITY_INSPECTION_NOT_FOUND"),
  },
});

route({
  method: "get",
  path: "/v1/quality/inspections/{id}/rework-target",
  operationId: "getQualityReworkTarget",
  summary:
    "The step a rework would re-open, and whether module 04 would re-open it (§3.5a preview)",
  permission: "quality.resolution.rework",
  description:
    "D-299 R8 — a READ that previews §3.5a's rework door. The target is chosen by the door's own rule: the `work_order_operation_id` query when given (the §4.4 failure sheet's explicit target — a final-stage failure sent back to the step that made it), else the inspection's own operation. It answers that step, the status of the WORK ORDER that owns it, its `qc_hold`, and `blocked_by`: null exactly when module 04's re-open core would re-open the step now, otherwise `work_order_not_live` (the order is not `released`/`in_progress`) or `operation_not_completed`, the same values the resolve and disposition doors' `409 QUALITY_REWORK_TARGET_NOT_REOPENABLE` carry. It exists because the door decides on the TARGET's order, and the inspection detail's `rework_target_work_order_status` describes only the inspection's own step. PRINCIPAL-AWARE (D-299 round 4, owner ruling Q4): `permitted` says whether THIS caller passes every authority layer the named `door` checks for this rework — `production.qc_hold.release` on a held step included (D-133) — and `holder_roles` names the seed roles that do, with `requires_hold_release` saying the hold is why; it is the same predicate the doors' refusals use for `continuations[].permitted`. `door` defaults to `inspection_resolve` (§4.4's sheet); §4.6's quarantine dialog sends `hold_disposition`, whose rework is `quality.hold.disposition` narrowed by role lineage. Advisory and unlocked: the order can complete after it, and the door stays the authority. Carries the rework door's route code, so it answers the principals who could send a rework, and the detail read's §7 stage fence applies. The two 422s are the door's own, from the same function.",
  request: {
    params: IdParam,
    query: z.object({
      /** An explicit target, as the resolve/disposition body would carry it. */
      work_order_operation_id: z.guid().optional(),
      /** The door whose authority `permitted` is judged on (D-299 Q4). */
      door: QualityFailureWorkflowDoor.optional(),
    }),
  },
  responses: {
    ...ok("The target, its order's status and module 04's verdict", QualityReworkTargetResponse),
    404: problemResponse("QUALITY_INSPECTION_NOT_FOUND"),
    422: problemResponse(
      "QUALITY_SUBJECT_INVALID — neither the query nor the inspection names an operation, or the named operation does not resolve in this tenant (the rework door's own two refusals) / VALIDATION_FAILED — a non-uuid id, or a `door` that is not one of the two",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/inspections/{id}/results",
  operationId: "recordQualityInspectionResult",
  summary: "Record one check-item result",
  permission: "quality.inspection.record",
  description:
    "Spec 07 §3.4 — the first result moves the inspection `open → in_progress`. REPEATABLE AND IDEMPOTENT BY CONSTRUCTION: the write is an UPSERT on `(inspection_id, check_item_id)` (§2.6's identity), so a floor device that flushes the same result twice converges on ONE row instead of appending a second, and no client key column is needed for it — the platform's `Idempotency-Key` header carries the response replay. MEASUREMENT AUTO-GRADING: `result` is computed as pass iff `min_value ≤ measured_value ≤ max_value` (a missing bound is unbounded on that side) and echoed back. A computed FAIL can never be overridden to pass (M07-S09 asserts no control exists for it and the server refuses); a computed pass may be manually failed with a note — visual judgment, the only direction that adds information.",
  request: { params: IdParam, ...jsonBody(QualityInspectionResultRequest) },
  responses: {
    ...ok("The stored result with the computed verdict", QualityInspectionResultResponse),
    404: problemResponse("QUALITY_INSPECTION_NOT_FOUND / QUALITY_CHECK_ITEM_NOT_FOUND"),
    409: problemResponse("QUALITY_INSPECTION_NOT_ACTIONABLE — the inspection is already terminal"),
    422: problemResponse(
      "QUALITY_MEASUREMENT_REQUIRED / QUALITY_RESULT_OVERRIDE_FORBIDDEN / QUALITY_EVIDENCE_INVALID",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/inspections/{id}/complete",
  operationId: "completeQualityInspection",
  summary: "Complete an inspection pass/fail",
  permission: "quality.inspection.record",
  description:
    "Spec 07 §3.4 — `passed` when every required item is `pass` (optional items may be `na`), `failed` when any required item is `fail`, in which case `failure_cause_id` is mandatory. The completion transaction is where the §3.2 gate is settled: a pass CLEARS module 04's operation `qc_hold` and a fail SETS it, through module-04-owned `clearOperationQcHoldInTxn` / `setOperationQcHoldInTxn` called from THIS module's transaction — never over the outbox relay, which is at-least-once and unordered, and never through module 04's manual-release endpoint, whose audit row asserts that a human overrode a quality decision (D-123 ruling 4). Two devices completing the same inspection: the second gets 409 with the winning state, so the PWA can say “completed by {name} while you were offline” and discard its queued action rather than duplicate it (§9.1).",
  request: { params: IdParam, ...jsonBody(QualityInspectionCompleteRequest) },
  responses: {
    ...ok(
      "The completed inspection and its side-effect summary",
      QualityInspectionCompleteResponse,
    ),
    404: problemResponse("QUALITY_INSPECTION_NOT_FOUND"),
    409: problemResponse(
      "QUALITY_INSPECTION_ALREADY_COMPLETED / QUALITY_INSPECTION_NOT_ACTIONABLE",
    ),
    422: problemResponse(
      "QUALITY_INSPECTION_INCOMPLETE / QUALITY_FAILURE_CAUSE_REQUIRED / QUALITY_FAILURE_CAUSE_NOT_FOUND / QUALITY_EVIDENCE_INVALID",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/inspections/{id}/cancel",
  operationId: "cancelQualityInspection",
  summary: "Cancel an inspection with a reason",
  permission: "quality.inspection.cancel",
  description:
    "Spec 07 §3.4 — `open`/`in_progress` → `cancelled` when the subject was scrapped elsewhere or the row is a duplicate. The reason is mandatory and stored: a cancelled check with no reason is indistinguishable from one nobody wanted to fail. Cancelling clears the §3.2 gate through the same in-transaction seam a resolution does, so no operation is left waiting (§9.7).",
  request: { params: IdParam, ...jsonBody(QualityInspectionCancelRequest) },
  responses: {
    ...ok("The cancelled inspection", QualityInspection),
    404: problemResponse("QUALITY_INSPECTION_NOT_FOUND"),
    409: problemResponse("QUALITY_INSPECTION_NOT_ACTIONABLE — already terminal"),
  },
});

route({
  method: "post",
  path: "/v1/quality/inspections/{id}/resolve",
  operationId: "resolveQualityInspection",
  summary: "Launch a failure workflow: rework, scrap or accept-deviation (§3.5)",
  permission: "quality.resolution.rework",
  description:
    "Spec 07 §3.5 — resolves a FAILED, resolution-pending inspection through one of the three non-cut workflows. AUTHORITY IS TWO-LAYER (D-040): the route carries ONE code — the register's one-code-per-route invariant — and kind `scrap` and `accept_deviation` ADDITIONALLY require `quality.resolution.dispose`, service-enforced, which is §7's row exactly: rework is fa/qc/planner, scrap/accept-deviation qc only. REWORK (§3.5a) re-opens the producing operation through module 04's own seam (`completed → in_progress`, never a spawned step — D-123 ruling 2); a caller holding `production.qc_hold.release` clears the operation's `qc_hold` in the same transaction, and a caller WITHOUT it on a HELD operation is refused 409 (D-133 — the planner's rework on an unheld operation, the common case, proceeds normally). SCRAP (§3.5b) posts to module 05's ledger through the in-txn seam (`source='qc_disposition'`, category `DEFECT_SCRAP`, the cause mapped through the vertical pack — an unmapped cause is 422, naming it) after the C6 double-count fence asks module 04 whether a scrap-disposed defect hand-off already covers the operation (409, refuse-never-net). ACCEPT DEVIATION (§3.5e) takes no stock action and makes the unit shippable by design. EVERY kind clears module 04's gate through the §3.2(b) in-txn seam (§9.7 — unconditional, a no-op when nothing was held) and resolves the inspection's hold where the kind disposes it (T3/T4). Idempotent on the `Idempotency-Key` header: a replay returns the ORIGINAL refs — the stable `clientEventId` derived from the inspection id means a replayed scrap replays the original ledger row rather than posting a second (§9.1).",
  request: { params: IdParam, ...jsonBody(QualityInspectionResolveRequest) },
  responses: {
    ...ok("The resolution refs (§5's “→ resolution refs”)", QualityResolutionResponse),
    404: problemResponse("QUALITY_INSPECTION_NOT_FOUND"),
    409: problemResponse(
      "QUALITY_INSPECTION_NOT_ACTIONABLE — not failed+pending / QUALITY_REWORK_REQUIRES_HOLD_RELEASE — a held operation the core WOULD re-open, and no release authority (D-133) / QUALITY_REWORK_TARGET_NOT_REOPENABLE — nothing re-opens, and `blocked_by` names which of two reasons: `operation_not_completed` (R4: the target operation is not `completed`) or `work_order_not_live` (D-299: its work order is not `released`/`in_progress`); EITHER is answered BEFORE the D-133 check (D-299 G1/R2), so nobody is sent to a releaser who would then be refused; `operation_status` and `work_order_status` quote the statuses decided on, and the detail names this door's continuations, accept_deviation and scrap — scrap ONLY when §3.5b's own fences (the C6 hand-off read, the R2 ledger read, the D-132 cause mapping, the subject's quantity) would admit it now, and otherwise a sentence naming the fence (D-299 Q3: one evaluator for the door and the list) / QUALITY_SCRAP_ALREADY_ATTRIBUTED — the C6 fence, naming the hand-off, its disposer and instant — OR (subject grain) a prior QC scrap already covering the unit/lot/operation, naming the waste event; its way on names accept_deviation, rework only while the inspection's own step would re-open, and downgrade only when a quarantine is part of the resolution (D-299 R7). Both refusals' way on is PRINCIPAL-AWARE (D-299 R3): an act the caller cannot take is not offered as theirs, its holders are named instead, and the `continuations` extension lists each act as `{kind, door, permitted, holder_roles}` / QUALITY_HOLD_NOT_ACTIVE — a concurrent disposition won the inspection's hold (§9.2, naming the winner)",
    ),
    422: problemResponse(
      "QUALITY_SCRAP_CAUSE_UNMAPPED — the failure cause carries no waste-cause mapping (named with its id and the mapping door in the title; never an UNKNOWN fallback) / QUALITY_SUBJECT_INVALID — among others, a scrap with no honest quantity to post (an operation subject without `quantity_inspected`, a unit with no remaining measure, a lot with nothing on hand). Both scrap refusals carry the way on in `detail` and the `continuations` extension, listing only acts whose own fences admit them now and worded for this principal (D-299 Q3/R3); the cause refusal also carries `failure_cause_id`",
    ),
  },
});

// ===========================================================================
// the ship-readiness gate (spec 07 §5, §3.5e)
// ===========================================================================

route({
  method: "get",
  path: "/v1/quality/ship-readiness",
  operationId: "getQualityShipReadiness",
  summary: "Per-unit ship gate (module 09's pre-ship read)",
  permission: "quality.inspection.read",
  description:
    "Spec 07 §5 — `ready` is true when the unit's latest FINAL inspection passed, or failed with resolution `accept_deviation`, or its quarantine was released with disposition `release` (the third disjunct, live since 07-B-2 — the D-129 pending note discharged), AND no active hold covers the unit or its lot. An accepted deviation is shippable BY DESIGN (§3.5e). Module 09 does not exist, so nothing calls this yet: it is registered as a READ because that is the whole of the contract 09 will consume, it is drivable by API test today, and M07-S05 is amended in place to assert the flip from `ready: false` to `ready: true` across an inspection rather than a ship confirmation no module can perform (§0.1). `hold_ids` names the ACTIVE holds covering the unit or its lot (07-B-1).",
  request: {
    query: z.object({
      /** Comma-separated stock unit ids (≤200), the bulk shape module 09 needs
       * to gate a whole shipment in one call rather than N. */
      stock_unit_ids: z.string().min(1),
    }),
  },
  responses: {
    ...ok("Per-unit gate status", QualityShipReadinessResponse),
    422: problemResponse("QUALITY_SUBJECT_INVALID — a stock unit id does not resolve"),
  },
});

// ===========================================================================
// quarantine holds (spec 07 §2.7, §3.5c, §5 — slice 07-B-1: list, place,
// detail; slice 07-B-2: disposition)
// ===========================================================================

route({
  method: "get",
  path: "/v1/quality/holds",
  operationId: "listQualityHolds",
  summary: "The quarantine list (§4.6)",
  permission: "quality.hold.read",
  description:
    "Spec 07 §5 — the Quarantine Board's list. THE USER-FACING WORD IS QUARANTINE (D-123 ruling 9): the path keeps `holds` because renaming it is an oasdiff break, and every rendered string says quarantine. Cursor-paginated, newest first, ≤200 per page — an unbounded collection, unlike the two catalogs above. `min_age_days` keeps only holds placed at least that many days ago (the board's aging filter); `product_id` and `supplier_id` resolve through the held subject's lot/receipt lineage, service-side. MONEY (D-126): `value_at_risk_minor` and `currency` are BOTH absent — never null — for principals outside group_admin/facility_admin/finance/qc/planner.",
  request: {
    query: z.object({
      status: QualityHoldStatus.optional(),
      facility_id: z.guid().optional(),
      product_id: z.guid().optional(),
      supplier_id: z.guid().optional(),
      min_age_days: z.coerce.number().int().min(0).max(3650).optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok("The quarantine page", QualityHoldListResponse),
});

route({
  method: "post",
  path: "/v1/quality/holds",
  operationId: "placeQualityHold",
  summary: "Place a manual quarantine hold on a stock unit or lot",
  permission: "quality.hold.place",
  description:
    "Spec 07 §2.7 — quarantines EXACTLY ONE subject, stock_unit_id XOR lot_id. The stock is quarantined through module 06's owned quarantine door IN THE SAME TRANSACTION (hold_id passed as the reason ref), and module 06 blocks issue/pick/ship from that moment — never over the outbox relay. A subject already under an active hold gets NO second row: the service appends the new reason to the existing hold (audit row) and returns it, because §2.7's partial uniques make a second active hold per unit or per lot unrepresentable.",
  request: jsonBody(QualityHoldPlaceRequest),
  responses: {
    ...created(
      "The placed hold — or the existing active hold the reason was attached to",
      QualityHold,
    ),
    422: problemResponse(
      "QUALITY_SUBJECT_INVALID — the subject does not resolve through module 06 (or the sent facility_id contradicts it) / FACILITY_NOT_FOUND",
    ),
  },
});

route({
  method: "get",
  path: "/v1/quality/holds/{id}",
  operationId: "getQualityHold",
  summary: "Quarantine hold detail with its linked inspection",
  permission: "quality.hold.read",
  description:
    "Added to §5 by 07-B decision D8 — a deep link (exception feed, Telegram push, a quarantine-board row opened in a new tab) cannot be served from a cursor list. Returns the hold plus a COMPACT summary of the inspection that placed it; `inspection` is null on a manually placed hold. Money redaction as on the list (D-126 — both keys absent together).",
  request: { params: IdParam },
  responses: {
    ...ok("The hold", QualityHoldDetailResponse),
    404: problemResponse(
      "QUALITY_HOLD_NOT_FOUND (cross-tenant and out-of-fence ids are invisible)",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/holds/{id}/disposition",
  operationId: "dispositionQualityHold",
  summary: "Release or dispose a quarantine hold (§3.5c)",
  permission: "quality.hold.disposition",
  description:
    "Spec 07 §3.5c — the one terminal transition an active hold takes: `release` → `released`; `rework`/`scrap`/`downgrade`/`return_to_supplier` → `disposed`. PROCUREMENT IS NARROWED TO `return_to_supplier` ONLY, service-enforced on top of the single code (D-040's two-layer pattern; §7's cell). The transition is one optimistic `UPDATE … WHERE status='active'` (§9.2): a zero-row result is 409 naming the WINNER, its disposition and its instant, so §4.6's concurrent-disposition state renders from the problem body. In the SAME transaction: module 06's stock flip through its owned in-txn arms (release returns the unit to `filled` / clears the lot — SKIPPED while any other active hold still covers the unit or its lot, §2.7's overlapping-hold rule; a lot-scoped scrap/return_to_supplier sets `lots.qc_status='rejected'` through `LotsService.rejectInTxn` — D-128 r2), module 04's `qc_hold` cleared through the §3.2(b) seam on EVERY terminal path (§9.7 — for `rework` the clear is conditional on the CALLER holding `production.qc_hold.release`, D-123 ruling 3), the linked inspection's resolution pair written (`resolution_kind` records the WORKFLOW — `rework`/`scrap` for the kinds that have one, `hold_disposition` for release/downgrade/return_to_supplier — D-134), any agent `proposed_*` quartet cleared (D-128 r4), and the event emitted (`quality.hold_released` / `quality.hold_disposed`). A `scrap` posts module 05's ledger row through the in-txn seam behind the C6 fence; a subject whose module-06 state refuses the flip is a 422 the dialog names, never a silent skip.",
  request: { params: IdParam, ...jsonBody(QualityHoldDispositionRequest) },
  responses: {
    ...ok("The terminal hold row (released or disposed)", QualityHold),
    404: problemResponse(
      "QUALITY_HOLD_NOT_FOUND (cross-tenant and out-of-fence ids are invisible)",
    ),
    409: problemResponse(
      "QUALITY_HOLD_NOT_ACTIVE — §9.2's optimistic guard lost; names the winner, their disposition and its instant / QUALITY_SCRAP_ALREADY_ATTRIBUTED — the C6 fence (§3.5b): a scrap-disposed defect hand-off already covers the operation, or a prior QC scrap already covers the subject; refuse, never net; its way on names rework (only while the inspection's own step would re-open) and downgrade — never accept_deviation, which this route does not take (D-299 R7) / QUALITY_REWORK_REQUIRES_HOLD_RELEASE — a rework by a principal without production.qc_hold.release on a HELD operation the core would re-open (D-133) / QUALITY_REWORK_TARGET_NOT_REOPENABLE — nothing re-opens, and `blocked_by` names which of two reasons: `operation_not_completed` (R4: the target operation is not `completed`) or `work_order_not_live` (D-299: its work order is not `released`/`in_progress`); EITHER is answered BEFORE the D-133 check (D-299 G1/R2); `operation_status` and `work_order_status` quote the statuses decided on, and the detail names this door's continuations — release with its justification, scrap, downgrade — never accept_deviation, which this route does not take, and scrap only when §3.5b's own fences would admit it now (D-299 Q3). Both refusals' way on is principal-aware and rides the `continuations` extension (`{kind, door, permitted, holder_roles}` — D-299 R3)",
    ),
    422: problemResponse(
      "QUALITY_SCRAP_CAUSE_UNMAPPED — the inspection's failure cause has no waste-cause mapping (named — never an UNKNOWN fallback) / QUALITY_SUBJECT_INVALID — among others, a scrap with no honest quantity to post. Both scrap refusals carry the way on in `detail` and the `continuations` extension, listing only acts whose own fences admit them now, never accept_deviation on this route (D-299 Q3/R3/R7). A subject whose module-06 state refuses the flip surfaces module 06's own refusal — the seam hands back its own codes",
    ),
  },
});

// ===========================================================================
// the guided cut (spec 07 §2.8, §2.9, §3.5d, §5 — slice 07-C)
//
// FIVE ROUTES, ONE PERMISSION CODE (D-163), `Idempotency-Key` on all four
// mutations (§5's preamble). The whole surface is ONLINE-ONLY on both shells
// (D-166): nothing here queues, because a guided cut consumes a physical unit,
// mints two, posts a waste ledger row, creates two inspections and closes a
// third — there is no commutative projection a queue replay could reconcile,
// and a cable cannot be un-cut.
// ===========================================================================

route({
  method: "get",
  path: "/v1/quality/cut-events/{id}",
  operationId: "getQualityCutEvent",
  summary: "Cut detail with its segments (§5)",
  permission: "quality.cut.execute",
  description:
    "Spec 07 §5 — the §2.8 row plus `segments[]` in `seq` order (A-end first), with `inventory_cut_event_id` once executed (D-157). There is deliberately NO LIST route for cut events: discovery is `guided_cut.cut_event_id` on the inspection detail (D-162), because the inspection is the thing the operator has in their hand and a list of cut events is a screen nobody asked for. A segment's `new_stock_unit_id` and `retest_inspection_id` are null until execute stamps them (§3.5d steps 6 and 7), which is why §2.9's kind↔ref CHECK is one-directional — a WASTE segment never carries either, a STOCK segment carries them according to where it stands in the lifecycle.",
  request: { params: IdParam },
  responses: {
    ...ok("The cut event and its segments", QualityCutEventDetailResponse),
    404: problemResponse(
      "QUALITY_CUT_EVENT_NOT_FOUND (cross-tenant and out-of-fence ids are invisible)",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/inspections/{id}/cut-events",
  operationId: "proposeQualityCutEvent",
  summary: "Propose a guided cut from a failed inspection (§3.5d step 1)",
  permission: "quality.cut.execute",
  description:
    "Spec 07 §3.5d step 1 — the service computes the tiling and stores it `proposed`: segment A `[0, fault − trim]`, the waste piece `[fault − trim, fault + trim]` that module 06's cut posts to module 05, and segment B `[fault + trim, source_length]`. A stock segment shorter than the pinned point's `min_sellable_length_m` is proposed as WASTE instead; a NULL minimum imposes none (D-172). THERE IS NO SOURCE UNIT ON THIS WIRE, deliberately: the source is the inspection's own subject, which is what makes the execute path's cut-child test a statement about THIS inspection rather than about any unit a caller could name (D-155). `fault_position_m` is optional and defaults to the LOWEST fault among the inspection's FAILED results, tie-broken by check-item `seq` — the first fault an operator walking the cable from the A-end meets (D-171; 07-C is single-fault, and two faults are cut one at a time). `trim_allowance_m` may only be raised above the point's own value, service-validated. A propose SUPERSEDES any live cut on the same inspection, cancelling it in the same transaction with `cancelled_reason` saying so, so an operator who re-opens the screen after a re-measure is never stranded by a row they cannot see (D-162). ELIGIBILITY is §3.5d's: the pinned point carries `allow_guided_cut`, the subject is a length-measured stock unit, at least one failed result carries a `fault_position_m`, the inspection is `failed` with its resolution pending, and the source's genealogy depth is below 5 (the original unit is 0 — D-158).",
  request: { params: IdParam, ...jsonBody(QualityCutProposeRequest) },
  responses: {
    ...created("The proposed cut event and its segment preview", QualityCutEventDetailResponse),
    404: problemResponse("QUALITY_INSPECTION_NOT_FOUND"),
    422: problemResponse(
      "QUALITY_CUT_NOT_ELIGIBLE — §3.5d's eligibility fails (the inspection read's `guided_cut.reason` names which) / QUALITY_CUT_DEPTH_CAP — genealogy_depth(source) ≥ 5, the §9.4 salami-slicing fence; at the cap the floor control is DEAD WITH ITS REASON, never absent (D-167) / QUALITY_CUT_LENGTH_NOT_MEASURED — the source carries only a machine-claimed length (§9.6) / QUALITY_CUT_FAULT_OUTSIDE_WINDOW / QUALITY_CUT_SEGMENT_BELOW_MINIMUM / QUALITY_SUBJECT_INVALID",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/cut-events/{id}/confirm",
  operationId: "confirmQualityCutEvent",
  summary: "Lock the cut positions (§3.5d step 2)",
  permission: "quality.cut.execute",
  description:
    "Spec 07 §3.5d step 2 / §4.5 — `proposed → confirmed`, stamping `confirmed_at`/`confirmed_by`. The operator may drag the two cut handles OUTWARD only, never inward past the `fault ± trim` window, and the server RE-TILES from the adjusted boundaries: the client's arithmetic is one honest check that names the snapshot it came from, never the authority (§4.5). `adjustments` is optional because accepting the proposal unchanged is the ordinary case. Confirming is not executing — §4.5 puts a double-confirm on the execute below, and this row is the operator saying the marks on the drum match the lines on the screen.",
  request: { params: IdParam, ...jsonBody(QualityCutConfirmRequest) },
  responses: {
    ...ok("The confirmed cut event and its re-tiled segments", QualityCutEventDetailResponse),
    404: problemResponse("QUALITY_CUT_EVENT_NOT_FOUND"),
    409: problemResponse(
      "QUALITY_CUT_NOT_CONFIRMED — the cut is no longer `proposed`. The body names its status in `current_status`",
    ),
    422: problemResponse(
      "QUALITY_CUT_SEGMENTS_DO_NOT_TILE — the segments do not tile [0, source_length_m] exactly (they can, because the cut is modelled kerf-free — D-170) / QUALITY_CUT_SEGMENT_BELOW_MINIMUM — a stock segment is under `min_sellable_length_m` and must be re-marked waste / QUALITY_CUT_FAULT_OUTSIDE_WINDOW — a handle was dragged INWARD past the fault, which would sell the fault inside a half",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/cut-events/{id}/execute",
  operationId: "executeQualityCutEvent",
  summary: "Perform the split (§3.5d step 3) — one transaction",
  permission: "quality.cut.execute",
  description:
    "Spec 07 §3.5d step 3, IN ORDER, in ONE transaction — the load-bearing sequence, because a `confirmed` cut event sitting over physically-split stock is the failure this ordering exists to prevent. (1) Lock the cut event and refuse unless `confirmed` (§9.2's optimistic guard, the same shape as the hold race). (2) Re-read the source through module 06's own in-txn read — a changed measured length is 409 with `current_length_m` in the problem extensions, a claimed-only one is 422 (§9.6: extending machines lie, so all cut maths use the MEASURED length). (3) Re-validate the tiling SERVER-SIDE; the client's check is a courtesy. (4) THE QUARANTINE ARMS (D-155): an active LOT hold covering the source refuses 409; an active UNIT hold whose `inspection_id` IS this inspection is released in-transaction and disposed `guided_cut` at step 8; an active unit hold from a DIFFERENT inspection or placed by hand refuses 409; and a source `quarantined` with NO hold refuses 409 unless it is the cut child this very inspection was minted to retest — the test being the CONJUNCTION of the source being the `new_stock_unit_id` of a `qc_cut_segments` row whose `retest_inspection_id` IS the cut's own inspection AND that inspection carrying `origin='cut_retest'`. (5) Module 06's `cutInTxn` with one piece per segment, `reason='qc_fail'` and `kerf` OMITTED (D-170 — the trim window IS the physical allowance). (6) Stamp each stock segment's child unit, in `seq` order. (7) Mint one retest per stock segment, graded against the PARENT's pinned control-point version (D-164, never `is_current`). (8) The failure workflow `guided_cut` — dispose the hold if step 4 found one, resolve the inspection, and clear module 04's operation gate through the §3.2(b) in-txn seam, never through endpoint 32 whose audit row asserts a human override (§9.7). (9) `quality.cut_completed` to the outbox plus the audit row. (10) The cut event → `completed` with `inventory_cut_event_id`. THE INVARIANT THIS PRESERVES, checkable in one read: the ONLY way a quarantined unit becomes cuttable is inside a qc-authorised guided cut on that unit's OWN quality question, and the transaction that lifts the quarantine is the transaction that physically destroys the unit — a rollback leaves it quarantined and held exactly as it was. `waste_event_id` is NOT in this response (D-157): module 05 mints that ledger row in a post-commit consumer, so no synchronous response can carry it, and `inventory_cut_event_id` is the deterministic join key §2.9 states the derivation for.",
  request: { params: IdParam },
  responses: {
    ...ok(
      "The completed cut: its children, their retests and module 06's event id",
      QualityCutExecuteResponse,
    ),
    404: problemResponse("QUALITY_CUT_EVENT_NOT_FOUND"),
    409: problemResponse(
      "QUALITY_CUT_NOT_CONFIRMED — §9.2's guard: the cut is not `confirmed`, and the body names its status in `current_status` / QUALITY_CUT_LENGTH_CHANGED — module 06 reports a different measured length than the cut was planned against; `current_length_m` rides in the extensions so the screen recomputes / QUALITY_CUT_LOT_HELD — an active LOT hold covers the source's lot, and a cut that ignored a condemned cohort would launder a lot-level decision into two sellable children / QUALITY_CUT_SUBJECT_HELD — an active unit hold from a DIFFERENT inspection or placed by hand, named with its reason and its author; dispose or release it on the board, then cut / QUALITY_CUT_SUBJECT_QUARANTINED — quarantined with no `qc_holds` row and not this inspection's own cut child; the continuation is module 06's `release-unit` door under `inventory.quarantine`, which is why it takes its own code rather than reusing the one above",
    ),
    422: problemResponse(
      "QUALITY_CUT_LENGTH_NOT_MEASURED — a claimed-only source (§9.6); on the floor the control is dead with its reason AND the re-measure offered in place / QUALITY_CUT_SEGMENTS_DO_NOT_TILE / QUALITY_CUT_SEGMENT_BELOW_MINIMUM / QUALITY_CUT_FAULT_OUTSIDE_WINDOW",
    ),
  },
});

route({
  method: "post",
  path: "/v1/quality/cut-events/{id}/cancel",
  operationId: "cancelQualityCutEvent",
  summary: "Abandon a proposed or confirmed cut",
  permission: "quality.cut.execute",
  description:
    "Spec 07 §5 — `proposed|confirmed → cancelled` with a MANDATORY reason: a cut somebody staged over a customer's cable and then dropped, with no reason, is indistinguishable from a bug. SERVICE-NARROWED to {group_admin, facility_admin, qc} on top of the single code (D-163's D-040 two-layer pattern, as the disposition surface and module 06's own cut both do) — a cancel is a decision about somebody else's staged work. A floor_worker is not stranded by the narrowing: D-162's supersede rule means their next propose replaces the live one. A `completed` cut cannot be cancelled — the saw has already run.",
  request: { params: IdParam, ...jsonBody(QualityCutCancelRequest) },
  responses: {
    ...ok("The cancelled cut event", QualityCutEventDetailResponse),
    404: problemResponse("QUALITY_CUT_EVENT_NOT_FOUND"),
    409: problemResponse(
      "QUALITY_CUT_NOT_CONFIRMED — the cut is already terminal (`completed` or `cancelled`), so there is no live cut to abandon. The body names its status in `current_status`",
    ),
  },
});

// ===========================================================================
// 07-E — certificates of analysis / conformance (§2.10, §2.11, §3.6, §5)
// ===========================================================================
// SEVEN authenticated rows on THREE codes, straight off the permission
// catalog's 07-E allocation (D-074 one-code-per-DISTINCT-role-set):
//   * `quality.coa.draft` (fa/qc/sales + ga) carries every read a drafter
//     needs — template list, document list, draft create, draft preview.
//     Sales DRAFT and READ certificates for their customers; they do not
//     attest them.
//   * `quality.coa.issue` (qc + ga) carries issue AND revoke: attestation and
//     its withdrawal are one authority.
//   * `quality.coa_template.manage` (ga/fa/qc) carries the template mutation.
// The eighth row is this module's ONLY unauthenticated route, registered
// below with `security: []`.

route({
  method: "get",
  path: "/v1/quality/coa-templates",
  operationId: "listQualityCoaTemplates",
  summary: "CoA templates (§4.7 Templates tab)",
  permission: "quality.coa.draft",
  description:
    "Spec 07 §5 — the template library the §3.6 composer picks from. NOT paginated: templates are branding definitions, counted in ones. Carries `quality.coa.draft` rather than the manage code because §5's role cell (qc, sales, facility_admin) is the DRAFTER's set — sales must see templates to draft, and D-074's rule is one code per distinct role set.",
  responses: ok("Every template, active and not", QualityCoaTemplateListResponse),
});

route({
  method: "post",
  path: "/v1/quality/coa-templates",
  operationId: "upsertQualityCoaTemplate",
  summary: "Create or update a CoA template",
  permission: "quality.coa_template.manage",
  description:
    "Spec 07 §5's one template mutation row — CREATE/UPDATE in one door: `id` present updates that template, absent creates (§2.10). `logo_file_id` must be a stored `quality_coa_logo` file of this tenant and `product_id` must resolve through module 01 — both service-validated, which an FK cannot do (§2 conventions). ISSUED documents are untouched by any template edit: they render from their frozen `data_snapshot` (§2.11), so a rebrand never rewrites an attested certificate.",
  request: jsonBody(QualityCoaTemplateUpsertRequest),
  responses: {
    ...created("The template as stored", QualityCoaTemplate),
    409: problemResponse("QUALITY_COA_TEMPLATE_NAME_TAKEN — UNIQUE(tenant, name)"),
    422: problemResponse(
      "QUALITY_COA_TEMPLATE_INVALID — the logo is not a stored quality_coa_logo file, the product is unknown to module 01, or the update targets no existing template",
    ),
  },
});

route({
  method: "get",
  path: "/v1/quality/coa-documents",
  operationId: "listQualityCoaDocuments",
  summary: "Certificates (§4.7 Documents tab)",
  permission: "quality.coa.draft",
  description:
    "Spec 07 §5 — every revision, newest first; filters per the row (`status`, `lot_id`, `customer_id`, `shipment_id` — the last matches nothing until module 09 lands, honestly empty rather than refused). `public_url` is present only on issued revisions (§9.11: revocation deletes the token).",
  request: {
    query: z.object({
      status: QualityCoaDocumentStatus.optional(),
      lot_id: z.guid().optional(),
      customer_id: z.guid().optional(),
      shipment_id: z.guid().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
  },
  responses: ok(
    "One certificate page + its keyset cursor (§5 preamble)",
    QualityCoaDocumentListResponse,
  ),
});

route({
  method: "post",
  path: "/v1/quality/coa-documents",
  operationId: "draftQualityCoaDocument",
  summary: "Draft a certificate from a lot (§3.6 steps 1–3)",
  permission: "quality.coa.draft",
  description:
    "Spec 07 §3.6 — mints `COA-{YYYY}-{seq5}` from `qc_number_series` (§2.5), assembles `data_snapshot` (subject lot + its stock units; every `appears_on_coa` measurement across the relevant PASSED final/in-process inspections with min/target/max/actual; inspector names; product basics from module 01) and answers the draft WITH its gap report — §3.6 step 3's missing-data guard, one row per (unit, item) lacking a passed measurement. A gapped draft is CREATED, not refused: the composer's job is to show the gaps with their remedies (run the inspection, exclude the unit, or — conformance only — attest past them). The draft's snapshot is RE-ASSEMBLED at preview and at issue, so an inspection passed after drafting closes its gap without a new draft; only ISSUE freezes it (§2.11). Every P2 certificate drafts from a LOT — the shipment door arrives with module 09 (§0.1).",
  request: jsonBody(QualityCoaDraftRequest),
  responses: {
    ...created("The draft + its gap report", QualityCoaDraftResponse),
    422: problemResponse(
      "QUALITY_COA_SUBJECT_INVALID — the lot does not exist, carries no stock units to certify, or the template is inactive / VALIDATION_FAILED",
    ),
  },
});

route({
  method: "get",
  path: "/v1/quality/coa-documents/{id}/preview.pdf",
  operationId: "previewQualityCoaDocument",
  summary: "Render a certificate PDF (§3.6 step 4)",
  permission: "quality.coa.draft",
  description:
    "Spec 07 §5 — streamed `application/pdf`, `inline`. A DRAFT re-assembles its snapshot first (so the preview is current, and a gap closed since drafting shows closed) and renders with a DRAFT watermark; an ISSUED revision renders nothing — its frozen PDF is the document, served by redirect exactly as the public link serves it (§2.11: the PDF renders only from the snapshot, and re-rendering an attested certificate would mint a second original). A REVOKED revision is 409.",
  request: { params: IdParam },
  responses: {
    200: {
      description: "The draft preview PDF, watermarked DRAFT",
      content: { "application/pdf": { schema: z.string().openapi({ format: "binary" }) } },
    },
    302: {
      description:
        "An issued revision redirects to its frozen PDF (a 5-minute presigned URL), the same document the public link serves",
    },
    404: problemResponse("NOT_FOUND"),
    409: problemResponse("QUALITY_COA_STATUS_INVALID — a revoked revision has no preview"),
  },
});

route({
  method: "post",
  path: "/v1/quality/coa-documents/{id}/issue",
  operationId: "issueQualityCoaDocument",
  summary: "Freeze and issue (§3.6 step 4)",
  permission: "quality.coa.issue",
  description:
    "Spec 07 §3.6 step 4, in ONE transaction: re-assemble the snapshot, refuse 422 `QUALITY_COA_GAPS` if the guard still lists any (conformance may attest past `missing_measurement` gaps; a `not_passed` gap blocks BOTH kinds — attesting over a failed measurement is a false certificate), render the PDF from the frozen snapshot, register it (`quality_coa_pdf`, `publicResolve: false` — the registry hands readership back to this module), mint the 32-byte `public_token`, stamp `issued_by`/`issued_at`, emit `quality.coa_issued`. The response carries `public_url` — the unauthenticated customer link (§9.11).",
  request: { params: IdParam },
  responses: {
    ...ok("The issued revision, public link live", QualityCoaIssueResponse),
    404: problemResponse("NOT_FOUND"),
    409: problemResponse("QUALITY_COA_STATUS_INVALID — only a draft can be issued"),
    422: problemResponse("QUALITY_COA_GAPS — the gap rows ride in `extensions.gaps`"),
  },
});

route({
  method: "post",
  path: "/v1/quality/coa-documents/{id}/revoke",
  operationId: "revokeQualityCoaDocument",
  summary: "Revoke (+ optionally draft the next revision)",
  permission: "quality.coa.issue",
  description:
    "Spec 07 §3.6 step 5 — reason MANDATORY; the `public_token` is DELETED in the same UPDATE, so the customer link answers 404 from the commit on (§9.11 — the walkthrough's own assertion), while the frozen PDF row stays for the tenant's own audit trail. `reissue: true` drafts `revision + 1` under the SAME `coa_no` in the same transaction (the §2.11 partial unique admits it because the old revision is now `revoked`), with a fresh snapshot assembly. A DRAFT may also be revoked — that is the lifecycle's only exit, and an abandoned draft with no terminal state would be permanent debris. Emits `quality.coa_revoked`.",
  request: { params: IdParam, ...jsonBody(QualityCoaRevokeRequest) },
  responses: {
    ...ok("The revoked revision (+ the next draft when reissued)", QualityCoaRevokeResponse),
    404: problemResponse("NOT_FOUND"),
    409: problemResponse("QUALITY_COA_STATUS_INVALID — already revoked"),
  },
});

// The module's ONLY unauthenticated route (§5, §9.11) — registered bespoke
// because `route()` stamps the session security every other row needs.
// `security: []` — the token IS the credential: resolved through a SECURITY
// DEFINER function that returns nothing but the owning tenant and the PDF's
// file id, rate-limited 10/min/IP through the platform's Postgres-backed
// fixed-window throttle, answering 302 → a short-lived S3 presigned GET
// (D-123: no CloudFront signing exists on this platform — §0.1 names the CDN
// slice as successor work; every §9.11 property is the token's and the
// presign's, not a CDN's). Revocation deletes the token, so a revoked
// certificate 404s indistinguishably from an unknown one — no enumeration
// surface.
registry.registerPath({
  method: "get",
  path: "/v1/public/coa/{public_token}",
  operationId: "getPublicCoa",
  tags: ["quality"],
  summary: "Unauthenticated certificate fetch (customer link)",
  description:
    "302 → a short-lived presigned GET of the frozen PDF. 404 for an unknown, revoked or never-issued token; 429 with Retry-After past 10 requests/min/IP. The route has no session, membership or permission check, because the token is the credential. The tenant resolution that runs before every route on a tenant host still applies, so a suspended or purging tenant answers 403 TENANT_SUSPENDED and a purged one 410 TENANT_PURGED here as on every other path of its host (spec 16 §3.1.1).",
  security: [],
  request: { params: z.object({ public_token: z.string().min(1).max(128) }) },
  responses: {
    302: { description: "Redirect to the PDF (5-minute presigned URL)" },
    400: undeclaredQueryRefused,
    403: problemResponse(
      "TENANT_SUSPENDED (the host's tenant is suspended, at once or past its grace window, or is being purged)",
    ),
    404: problemResponse("NOT_FOUND (an unknown, revoked or never-issued token)"),
    410: problemResponse("TENANT_PURGED (the host's tenant is permanently deleted)"),
    429: problemResponse("TOO_MANY_ATTEMPTS (10 requests/min/IP, Retry-After in seconds)"),
  },
});

// ===========================================================================
// 07-E — the analytics reads (§2.12, §5's four rows + the D-216-B door)
// ===========================================================================
// FIVE routes, ONE code (`quality.analytics.read`) — §5's four role cells are
// four DIFFERENT sets over one analytics surface, and D-074's rule would mint
// four codes for four reads of one cube; the register's one-code-per-route
// invariant + D-040's two layers resolve it the way the cut surface did: the
// single code gates the SURFACE, and each route's §5 cell is enforced
// service-side (floor_worker: scoreboard only; procurement: supplier panel
// only; the refresh door: group_admin only — D-216-B). The §7 money pair on
// the Pareto's value view is redacted per D-126's set, both-or-neither.

const dateQuery = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);

route({
  method: "get",
  path: "/v1/quality/analytics/fpy",
  operationId: "getQualityFpy",
  summary: "FPY series off the §2.12 projection (§4.8)",
  permission: "quality.analytics.read",
  description:
    "Spec 07 §5 — buckets from `qc_fpy_daily_v` (the security-barrier wrapper; the API never reads the matview directly). `group_by ∈ day|product|work_center|shift` (`day` is the §4.8 trend's grain) — `step` is DEFERRED with the grain dimension (D-216 pt 3). `refreshed_at` is the projection's own staleness stamp (§4.8 renders it beside the chart; D-216-B). floor_worker and procurement are refused service-side — their §5 cells cover the scoreboard and the supplier panel alone (D-040 two-layer on the one analytics code).",
  request: {
    query: z.object({
      from: dateQuery,
      to: dateQuery,
      group_by: z.enum(["day", "product", "work_center", "shift"]),
      facility_id: z.guid().optional(),
    }),
  },
  responses: ok("The FPY buckets + the projection's staleness stamp", QualityFpyResponse),
});

route({
  method: "get",
  path: "/v1/quality/analytics/failure-pareto",
  operationId: "getQualityFailurePareto",
  summary: "Top failure causes, by count or by value at risk (§4.8)",
  permission: "quality.analytics.read",
  description:
    "Spec 07 §5 — causes ranked over the window. `by=count` counts FAILED inspections per cause from the live rows; `by=value` sums the quarantine board's stamped `value_at_risk_minor` through each hold's deciding inspection — the money the failures actually put at risk, not a re-derivation. THE §7 MONEY PAIR IS REDACTED both-or-neither (D-126): a caller outside group_admin/facility_admin/finance/qc/planner gets `count` ranking and NO value fields even when `by=value` — the ranking survives, the money does not (M07-S13's floor_worker leg).",
  request: {
    query: z.object({
      from: dateQuery,
      to: dateQuery,
      facility_id: z.guid().optional(),
      by: z.enum(["count", "value"]).optional(),
    }),
  },
  responses: ok("The ranked causes", QualityParetoResponse),
});

route({
  method: "get",
  path: "/v1/quality/analytics/shift-scoreboard",
  operationId: "getQualityShiftScoreboard",
  summary: "Per-shift quality table for one date (§4.8)",
  permission: "quality.analytics.read",
  description:
    "Spec 07 §5 — one row per shift instance on the date (module 04's own §9.7 shift-date semantics, read through ProductionModule's exported services — never a foreign table): inspections DETECTED in the shift, defects PRODUCED by it (§9.9's two stamps, different questions), defect hand-off incidents (module 04's count, through its service), and the shift's FPY. floor_worker reaches THIS route alone of the four (§5's cell), own facility, no value fields anywhere on it.",
  request: {
    query: z.object({ date: dateQuery, facility_id: z.guid() }),
  },
  responses: ok("The per-shift rows", QualityShiftScoreboardResponse),
});

route({
  method: "get",
  path: "/v1/quality/analytics/supplier-receiving",
  operationId: "getQualitySupplierReceiving",
  summary: "Receiving failure rates per supplier (§4.8)",
  permission: "quality.analytics.read",
  description:
    "Spec 07 §5 — receiving-stage inspections grouped by the D-192 `supplier_id` stamp (lots.supplier_id until module 10 lands, §0.1's spelling): receipts sampled, failures, failure rate, top cause. qc, procurement and group_admin only — service-narrowed on the one analytics code (D-040); the SCORECARD stays module 10's (D-200).",
  request: { query: z.object({ from: dateQuery, to: dateQuery }) },
  responses: ok("The per-supplier rows", QualitySupplierReceivingResponse),
});

route({
  method: "post",
  path: "/v1/quality/analytics/refresh",
  operationId: "refreshQualityAnalytics",
  summary: "Force-refresh the §2.12 projection (D-216-B)",
  permission: "quality.analytics.read",
  description:
    "Spec 07 §2.12 as amended (D-216-B) — the admin door: calls the SECURITY DEFINER `qc_fpy_refresh(0)`, bypassing the outbox worker's 15-minute debounce, and answers the new staleness stamp. GROUP_ADMIN ONLY, service-narrowed on the analytics code (D-040 two-layer — a fifth code for one route's set of one would be the cost D-163 refused). Exists for the night runner and the M07-S13 walkthrough's determinism: a demo must not wait out a debounce window to see its own seed.",
  request: {},
  responses: {
    ...ok("The projection is fresh", QualityAnalyticsRefreshResponse),
  },
});
